import { useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Search, Download, Trash2, ShieldCheck, FileText, MessageSquare } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—");

const DOC_STATUS = { pending: "En attente", reviewed: "Examiné", approved: "Approuvé", rejected: "Refusé" };

export const AdminRgpd = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/rgpd/search`, { params: { email: email.trim() }, headers });
      setResult(res.data);
    } catch (error) {
      toast.error(error.response?.status === 422 ? "Adresse e-mail invalide" : "Recherche impossible");
    } finally {
      setLoading(false);
    }
  };

  const exportZip = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/admin/rgpd/export`, { params: { email: result.email }, headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `rgpd_${result.email.replace(/[^a-z0-9_.-]/gi, "_")}.zip` });
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé (JSON + pièces PDF)");
    } catch (error) {
      toast.error("Export impossible");
    } finally {
      setBusy(false);
    }
  };

  const deleteAll = async () => {
    setBusy(true);
    try {
      const res = await axios.delete(`${API}/admin/rgpd`, { params: { email: result.email }, headers });
      toast.success(`Suppression effectuée : ${res.data.contacts_deleted} demande(s), ${res.data.documents_deleted} dossier(s)`);
      setResult({ ...result, contacts: [], documents: [] });
    } catch (error) {
      toast.error("Suppression impossible");
    } finally {
      setBusy(false);
    }
  };

  const total = result ? result.contacts.length + result.documents.length : 0;

  return (
    <AdminLayout>
      <div className="max-w-5xl space-y-8" data-testid="admin-rgpd-page">
        <div>
          <h1 className="font-serif text-3xl text-[#0A0A0A] mb-2">Données personnelles (RGPD)</h1>
          <p className="text-gray-600">Répondez aux demandes d'accès, de portabilité ou d'effacement : retrouvez toutes les données d'une personne à partir de son adresse e-mail, exportez-les ou supprimez-les en un clic.</p>
        </div>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><Search size={20} className="text-[#D4AF37]" />Rechercher une personne</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={search} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 space-y-2">
                <Label htmlFor="rgpd-email" className="sr-only">Adresse e-mail</Label>
                <Input id="rgpd-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="adresse@exemple.fr" required data-testid="rgpd-search-email" />
              </div>
              <Button type="submit" disabled={loading} className="bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]" data-testid="rgpd-search-submit">
                {loading ? "Recherche..." : "Rechercher"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {result && (
          <Card className="border-0 shadow-sm" data-testid="rgpd-results">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <ShieldCheck size={20} className="text-[#D4AF37]" />
                <span data-testid="rgpd-result-email">{result.email}</span>
                <Badge variant="secondary" data-testid="rgpd-result-count">{total} élément{total > 1 ? "s" : ""}</Badge>
              </CardTitle>
              {total > 0 && (
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={exportZip} disabled={busy} data-testid="rgpd-export-button">
                    <Download size={16} className="mr-2" />Exporter (ZIP)
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button type="button" variant="destructive" disabled={busy} data-testid="rgpd-delete-button">
                        <Trash2 size={16} className="mr-2" />Tout supprimer
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="rgpd-delete-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer définitivement les données de {result.email} ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {result.contacts.length} demande(s) et {result.documents.length} dossier(s) locatif(s) (fichiers PDF inclus) seront effacés de la base du site. Cette action est irréversible. Pensez à exporter d'abord si la personne a demandé une copie, et à supprimer les e-mails correspondants dans votre messagerie IONOS.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="rgpd-delete-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={deleteAll} className="bg-red-600 hover:bg-red-700" data-testid="rgpd-delete-confirm">Supprimer définitivement</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-8">
              {total === 0 && <p className="text-gray-500 italic" data-testid="rgpd-empty">Aucune donnée enregistrée sur le site pour cette adresse.</p>}

              {result.contacts.length > 0 && (
                <section>
                  <h3 className="flex items-center gap-2 font-medium mb-3"><MessageSquare size={16} className="text-[#D4AF37]" />Demandes (contact, estimation, gestion)</h3>
                  <ul className="divide-y divide-gray-100 border border-gray-100 rounded-sm">
                    {result.contacts.map((c) => (
                      <li key={c.id} className="p-4 text-sm" data-testid={`rgpd-contact-${c.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium">{c.subject}</span>
                          <span className="text-gray-500">{fmtDate(c.created_at)}</span>
                        </div>
                        <p className="text-gray-600 mt-1">{c.name}{c.phone ? ` · ${c.phone}` : ""}</p>
                        <p className="text-gray-500 mt-1 whitespace-pre-line line-clamp-3">{c.message}</p>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {result.documents.length > 0 && (
                <section>
                  <h3 className="flex items-center gap-2 font-medium mb-3"><FileText size={16} className="text-[#D4AF37]" />Dossiers locatifs</h3>
                  <ul className="divide-y divide-gray-100 border border-gray-100 rounded-sm">
                    {result.documents.map((d) => (
                      <li key={d.id} className="p-4 text-sm flex flex-wrap items-center justify-between gap-2" data-testid={`rgpd-document-${d.id}`}>
                        <div>
                          <p className="font-medium">{d.file_name} <span className="text-gray-400 font-normal">({d.file_size_kb} Ko)</span></p>
                          <p className="text-gray-600">{d.client_name}{d.purpose ? ` · Réf. ${d.purpose}` : ""}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{DOC_STATUS[d.status] || d.status}</Badge>
                          <span className="text-gray-500">{fmtDate(d.created_at)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminRgpd;
